import {
  type HTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { Icon } from './Icon';

const cx = (...parts: (string | false | undefined)[]) => parts.filter(Boolean).join(' ');

/* ---------- Field ---------- */
export interface FieldProps extends HTMLAttributes<HTMLDivElement> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  htmlFor?: string;
}

export function Field({ label, hint, error, htmlFor, children, className = '', ...rest }: FieldProps) {
  return (
    <div className={cx('zen-field', className)} {...rest}>
      {label && (
        <label className="zen-field__label" htmlFor={htmlFor}>
          {label}
        </label>
      )}
      {children}
      {error ? <span className="zen-field__error">{error}</span> : hint ? <span className="zen-field__hint">{hint}</span> : null}
    </div>
  );
}

/* ---------- Input ---------- */
export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  icon?: string;
  invalid?: boolean;
}

export function Input({ icon, invalid = false, className = '', ...rest }: InputProps) {
  const input = <input className={cx('zen-input', invalid && 'zen-input--invalid', className)} {...rest} />;
  if (!icon) return input;
  // The icon is positioned here (not via the design's `.zen-input-wrap .zen-icon`
  // CSS, which targeted the removed mask class).
  return (
    <span className="zen-input-wrap zen-input-wrap--icon">
      <Icon
        name={icon}
        size="16px"
        style={{ position: 'absolute', left: 'var(--space-5)', color: 'var(--grey-400)', pointerEvents: 'none' }}
      />
      {input}
    </span>
  );
}

/* ---------- Select ---------- */
type Option = string | { value: string; label: string };
export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  options?: Option[];
}

export function Select({ options = [], children, className = '', ...rest }: SelectProps) {
  return (
    <select className={cx('zen-select', className)} {...rest}>
      {children ||
        options.map((o) => {
          const value = typeof o === 'string' ? o : o.value;
          const label = typeof o === 'string' ? o : o.label;
          return (
            <option key={value} value={value}>
              {label}
            </option>
          );
        })}
    </select>
  );
}

/* ---------- Textarea ---------- */
export function Textarea({ rows = 4, className = '', ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea rows={rows} className={cx('zen-textarea', className)} {...rest} />;
}

/* ---------- Checkbox ---------- */
export interface ToggleProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: ReactNode;
}

export function Checkbox({ label, disabled = false, className = '', ...rest }: ToggleProps) {
  return (
    <label className={cx('zen-check', disabled && 'zen-check--disabled', className)}>
      <input type="checkbox" disabled={disabled} {...rest} />
      <span className="zen-check__box">
        <span className="zen-check__tick" />
      </span>
      {label && <span>{label}</span>}
    </label>
  );
}

/* ---------- Radio ---------- */
export function Radio({ label, disabled = false, className = '', ...rest }: ToggleProps) {
  return (
    <label className={cx('zen-check', 'zen-check--radio', disabled && 'zen-check--disabled', className)}>
      <input type="radio" disabled={disabled} {...rest} />
      <span className="zen-check__box">
        <span className="zen-check__dot" />
      </span>
      {label && <span>{label}</span>}
    </label>
  );
}

/* ---------- Switch ---------- */
export function Switch({ label, disabled = false, className = '', ...rest }: ToggleProps) {
  return (
    <label className={cx('zen-switch', disabled && 'zen-switch--disabled', className)}>
      <input type="checkbox" role="switch" disabled={disabled} {...rest} />
      <span className="zen-switch__track">
        <span className="zen-switch__thumb" />
      </span>
      {label && <span>{label}</span>}
    </label>
  );
}
